import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    TrialBalanceEmpty,
    TrialBalanceSkeleton,
    TrialBalanceTable,
    TrialBalanceTotals,
} from './components';
import {
    CLOSING_VIEWS,
    type ClosingView,
    type TrialBalanceRowView,
    type TrialBalanceTotalsView,
} from './trialBalanceMeta';

type TrialBalancePageViewProps = {
    asOfText: string;
    closingView: ClosingView;
    onClosingViewChange: (view: ClosingView) => void;
    closingNoteKey: string;
    rows: TrialBalanceRowView[];
    totals: TrialBalanceTotalsView | null;
    /** The report resolved and holds no rows — a real answer, not an unknown. */
    isEmpty: boolean;
    onViewLedger: (accountId: string) => void;
    generatedText: string;
    /**
     * The report is still in flight. Both controls and Print keep their real
     * labels and are disabled; only the totals and the rows skeleton.
     */
    isLoading: boolean;
};

/**
 * Trial balance — render only. The Books shell supplies the page title **and
 * the as-of date**, so unlike its `/accounting` counterpart this carries no date
 * control. What is left is the closing-entries reading, Print, the totals with
 * their verdict, and the table.
 *
 * The as-of date and the closing-entries note are stated **outside** the
 * `print:hidden` control row on purpose: on paper the shell's picker is gone
 * too, and a printed trial balance with no date on it — or one that does not say
 * it leaves the year-end close out — is not a document anyone should file.
 */
export const TrialBalancePageView: React.FC<TrialBalancePageViewProps> = ({
    asOfText,
    closingView,
    onClosingViewChange,
    closingNoteKey,
    rows,
    totals,
    isEmpty,
    onViewLedger,
    generatedText,
    isLoading,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('trialBalance.title')}
            className="flex flex-col gap-4"
        >
            <div className="flex flex-wrap items-end justify-between gap-4 print:hidden">
                <div className="flex flex-wrap items-end gap-4">
                    {/* No as-of control: the Books shell owns the period, and
                        this report takes its range end. The closing-entries
                        reading below is not a date, so it stays. */}
                    <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium uppercase tracking-wide text-muted">
                            {t('trialBalance.closing.label')}
                        </span>
                        <Segment
                            aria-label={t('trialBalance.closing.ariaLabel')}
                            isDisabled={isLoading}
                            onSelectionChange={(key) =>
                                onClosingViewChange(key as ClosingView)
                            }
                            selectedKey={closingView}
                            size="sm"
                            {...getNodeTestIdProp('trial-balance-closing')}
                        >
                            {CLOSING_VIEWS.map((view) => (
                                <Segment.Item
                                    id={view.id}
                                    key={view.id}
                                    {...getNodeTestIdProp(
                                        `trial-balance-closing-${view.id}`,
                                    )}
                                >
                                    <Segment.Separator />
                                    {t(view.labelKey)}
                                </Segment.Item>
                            ))}
                        </Segment>
                    </div>
                </div>
                <Button
                    isDisabled={isLoading}
                    onPress={() => window.print()}
                    variant="outline"
                    {...getNodeTestIdProp('trial-balance-print')}
                >
                    <Icon aria-hidden icon="gravity-ui:printer" />
                    {t('statements.print')}
                </Button>
            </div>

            <div className="flex flex-col gap-0.5">
                <span
                    className="text-sm font-semibold text-foreground"
                    {...getNodeTestIdProp('trial-balance-as-of-statement')}
                >
                    {t('trialBalance.asOfStatement', { date: asOfText })}
                </span>
                <span
                    className="text-xs text-muted"
                    {...getNodeTestIdProp('trial-balance-closing-note')}
                >
                    {t(closingNoteKey)}
                </span>
            </div>

            {isLoading ? <TrialBalanceSkeleton /> : null}

            {!isLoading && isEmpty ? (
                <TrialBalanceEmpty asOfText={asOfText} />
            ) : null}

            {!isLoading && !isEmpty && totals ? (
                <>
                    <TrialBalanceTotals
                        balanced={totals.balanced}
                        differenceText={totals.differenceText}
                        totalCredit={totals.creditText}
                        totalDebit={totals.debitText}
                    />
                    <TrialBalanceTable
                        footerNote={t('statements.footer.currencyNote')}
                        generatedText={t('statements.footer.generated', {
                            datetime: generatedText,
                        })}
                        onViewLedger={onViewLedger}
                        rows={rows}
                        totals={totals}
                        zeroNote={t('trialBalance.footer.zeroNote')}
                    />
                </>
            ) : null}
        </section>
    );
};
